import Close from "@mui/icons-material/Close";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import React, { useCallback, useState } from "react";

import { moduleType } from "../../__generated__/globalTypes";
import { useAppState } from "../../hooks/useAppState";
import { useCourseProgress } from "../../hooks/useCourseProgress";
import { useProgressModal } from "../../hooks/useProgressModal";
import { getFirstSectionIndex } from "../../util/course";
import DetailedProgressBar from "./DetailedProgressBar";
import {
  CompletionCert,
  DownloadCertButton,
  ModalHeaderContainer,
  ModalHeaderContent,
  ModalSubtitle,
  ModalTitle,
  ModuleProgress,
  ProgressContainer,
  QuizScore,
  SectionIndex,
  SectionTitle,
  StyledDialog,
  StyledDialogContent,
} from "./ProgressModal.elements";
import ProgressOverview from "./ProgressOverview";

export function ProgressModal(): React.ReactElement | null {
  const { setModule, course: outline } = useAppState();

  const { modalOpen, modalParams, closeModal } = useProgressModal();

  const { progress, openCert, expiration } = useCourseProgress(
    modalParams.courseId,
    modalParams.courseUuid,
    modalParams.instanceId,
  );

  const [downloadingCert, setDownloadingCert] = useState(false);

  const downloadCert = useCallback(async () => {
    setDownloadingCert(true);
    await openCert();
    setDownloadingCert(false);
  }, [openCert]);

  if (!progress) {
    return null;
  }

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLSpanElement>,
    id: string | null,
  ): void => {
    e.preventDefault();
    if (id) {
      closeModal();
      setModule(id);
    }
  };

  const { moduleProgress } = progress;
  const firstSectionIndex = getFirstSectionIndex(outline?.sections ?? []);

  const renderModuleContent = (module: (typeof moduleProgress)[number]): React.ReactNode => {
    if (module.type === moduleType.QUIZ) {
      if (!module.quizScore) return null;
      return <QuizScore pass={module.quizScore >= 80}>Best score: {module.quizScore}</QuizScore>;
    }

    return (
      <DetailedProgressBar
        compact={false}
        isExpired={false}
        isQuizProgress={false}
        complete={module.viewedSlides ?? 0}
        total={module.totalSlides ?? 0}
      />
    );
  };

  return (
    <StyledDialog open={modalOpen} onClose={() => closeModal()} maxWidth="md" aria-labelledby="progress_modal_title">
      <DialogTitle id="progress_modal_title" aria-label={`${outline?.name} course progress modal`}>
        <ModalHeaderContainer>
          <ModalHeaderContent>
            <ModalTitle>Progress</ModalTitle>
            {modalParams.fromLibrary && outline?.name && (
              <ModalSubtitle title={outline.name}>{outline.name}</ModalSubtitle>
            )}
          </ModalHeaderContent>
        </ModalHeaderContainer>
        <IconButton
          id="progress_modal_close"
          data-testid="progress_modal_close"
          aria-label="close modal"
          autoFocus
          onClick={() => closeModal()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <StyledDialogContent>
        <CompletionCert>
          <h3 className="title">Certificate of Completion</h3>
          {progress.complete ? (
            <DownloadCertButton loading={downloadingCert} onClick={() => downloadCert()}>
              Download
            </DownloadCertButton>
          ) : (
            <p className="content">Once you complete the course, your certificate can be accessed here.</p>
          )}
        </CompletionCert>
        <ProgressContainer>
          <ProgressOverview title="Overall Progress" progress={progress} expiration={expiration} />
        </ProgressContainer>
        <Divider />
        <ProgressContainer id="progress_modal_list">
          <Typography className="section-progress-title" variant="h5" component="h3">
            Section Progress
          </Typography>
          {outline?.sections.slice(firstSectionIndex).map((section, sectionIdx) => (
            <div key={section.id}>
              <SectionIndex>Section {sectionIdx + 1}</SectionIndex>
              <SectionTitle>{section.name}</SectionTitle>
              {moduleProgress
                .filter(progress => [moduleType.CONTENT, moduleType.QUIZ].includes(progress.type))
                .filter(module => section.modules.some(m => m.id === module.id))
                .map((module, idx) => (
                  <ModuleProgress
                    id={`module_list_item_${idx}`}
                    key={`module_list_item_${idx}`}
                    className="module_list_item"
                  >
                    <Link href="" onClick={e => handleClick(e, module.id)}>
                      <p className={module.type === moduleType.QUIZ ? "section-quiz" : "section-name"}>
                        {module.type !== moduleType.QUIZ
                          ? `${module.fullNumber} ${module.name}`
                          : `Section ${Math.abs(parseInt(module.fullNumber || "0"))} Quiz`}
                      </p>
                    </Link>
                    {renderModuleContent(module)}
                  </ModuleProgress>
                ))}
            </div>
          ))}
        </ProgressContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
}
